<div class="cos-bar" [class.expanded]="expanded()" [class.stuck]="stuckMode()">
  <!--
    Phase 1 (SPEC v2 §6.8) — breakdown preview card. Floats above the bar
    when Claude has returned a proposal, before the user has confirmed or
    discarded. The card is the entire commit surface: titles are editable
    in place, Discard is the no-op exit, Create commits the parent +
    subtasks atomically via /api/actions/with-subtasks.
  -->
  @if (breakdownProposal(); as proposal) {
    <div class="breakdown-preview" role="dialog" aria-label="Task breakdown proposal">
      <div class="bd-header">
        <span class="bd-title">{{ proposal.parentTitle }}</span>
        @if (proposal.suggestedTag) {
          <span class="bd-tag">{{ proposal.suggestedTag }}</span>
        }
        <span class="bd-total">{{ breakdownTotalMinutes() }} min total</span>
      </div>
      @if (proposal.reasoning) {
        <p class="bd-reasoning">{{ proposal.reasoning }}</p>
      }
      <ul class="bd-subtasks">
        @for (s of proposal.subtasks; track $index; let i = $index) {
          <li>
            <input
              type="text"
              class="bd-subtask-title"
              [value]="s.title"
              (input)="updateSubtaskTitle(i, $any($event.target).value)"
              [disabled]="breakdownConfirming()"
            />
            <span class="bd-est">{{ s.estimateMinutes }}m</span>
          </li>
        }
      </ul>
      <div class="bd-actions">
        <button
          type="button"
          class="primary"
          (click)="confirmBreakdown()"
          [disabled]="breakdownConfirming()"
        >
          @if (breakdownConfirming()) {
            <span class="spinner" aria-hidden="true"></span> Creating…
          } @else {
            Create {{ proposal.subtasks.length }} task{{ proposal.subtasks.length === 1 ? '' : 's' }}
          }
        </button>
        <button
          type="button"
          class="quiet"
          (click)="discardBreakdown()"
          [disabled]="breakdownConfirming()"
        >
          Discard
        </button>
      </div>
    </div>
  }

  @if (expanded() && visibleHistory().length > 0) {
    <div class="thread">
      <button class="thread-close" type="button" (click)="clearHistory()" title="Clear conversation">×</button>
      @for (turn of visibleHistory(); track $index) {
        <div class="turn" [class.user]="turn.role === 'User'" [class.assistant]="turn.role === 'Assistant'">
          @if (turn.role === 'Assistant' && turn.toolInvocations && turn.toolInvocations.length > 0) {
            @if (chipInvocations(turn.toolInvocations).length > 0) {
              <div class="receipts">
                @for (tool of chipInvocations(turn.toolInvocations); track $index) {
                  <span class="receipt" [attr.data-kind]="toolKind(tool)">{{ toolLabel(tool) }}</span>
                }
              </div>
            }
            @for (tool of recallInvocations(turn.toolInvocations); track $index) {
              <div class="recall">
                @for (entry of tool.recallEntries!; track entry.id) {
                  <article class="recall-card">
                    @if (entry.headline) {
                      <div class="headline">{{ entry.headline }}</div>
                    }
                    <div class="excerpt">{{ entry.content }}</div>
                    <div class="meta">{{ entry.notebookTitle }} · {{ entry.createdAt | date:'short' }}</div>
                  </article>
                }
              </div>
            }
          }
          <div class="content">{{ turn.content }}</div>
        </div>
      }
      @if (history().length > MAX_VISIBLE_TURNS) {
        <div class="muted">+{{ history().length - MAX_VISIBLE_TURNS }} earlier</div>
      }
    </div>
  }

  @if (error()) {
    <div class="error">{{ error() }}</div>
  }

  @if (draftHits().length > 0 && !busy()) {
    <div class="draft-hints" [class.expanded]="draftHintsExpanded()">
      <button
        type="button"
        class="draft-hints-toggle"
        (click)="toggleDraftHints()"
        [attr.aria-expanded]="draftHintsExpanded()"
      >
        <span class="dot" aria-hidden="true"></span>
        You've written about this before — {{ draftHits().length }} note{{ draftHits().length === 1 ? '' : 's' }}
        <span class="caret" aria-hidden="true">{{ draftHintsExpanded() ? '▾' : '▸' }}</span>
      </button>
      @if (draftHintsExpanded()) {
        <div class="recall">
          @for (entry of draftHits(); track entry.id) {
            <article class="recall-card">
              @if (entry.headline) {
                <div class="headline">{{ entry.headline }}</div>
              }
              <div class="excerpt">{{ entry.content }}</div>
              <div class="meta">{{ entry.notebookTitle }} · {{ entry.createdAt | date:'short' }}</div>
            </article>
          }
        </div>
      }
    </div>
  }

  <!--
    Phase 1 (SPEC v2 §6.8) — two-column bar.
      Left  (composer): existing COS NLP. Will host current-task + timer
                        when a session is active (Step 3, not yet wired).
      Right (breakdown-pane): always-present "what needs breaking?" input,
                        focusable from anywhere via Cmd+Shift+B.
    Stacks vertically on mobile (see .scss media query).
  -->
  <div class="bar-row">
    <!--
      Phase 1 (SPEC v2 §6.8 Step 3) — when a timer is running, the left half
      of the bar shows what's running + live elapsed instead of the COS
      composer. Clicking the title navigates to the Focus cockpit; pause
      ends the session. Composer returns the instant the timer stops.
    -->
    @if (timer.active(); as session) {
      <div class="running" role="region" aria-label="Active timer">
        <button
          type="button"
          class="running-title"
          (click)="openRunningAction()"
          [attr.aria-label]="'Open focus mode for ' + session.actionItemTitle"
          title="Open Focus Mode for this task"
        >
          <span class="running-dot" aria-hidden="true"></span>
          <span class="running-text">{{ session.actionItemTitle }}</span>
        </button>
        <span class="running-elapsed" aria-live="polite">{{ formatElapsed(timer.elapsedSeconds()) }}</span>
        <button
          type="button"
          class="running-stop"
          (click)="stopTimer()"
          aria-label="Stop timer"
          title="Stop"
        >
          <span aria-hidden="true">⏸</span>
        </button>
        <!--
          CoS icon button — collapsed entry point to the composer while the
          timer is running. Tap to pop a floating panel with the full textarea
          + ease + send buttons. Composer state (draft text, history) is
          preserved across collapses because the bar's signals don't reset.
        -->
        <button
          type="button"
          class="running-cos"
          (click)="toggleComposer()"
          [class.active]="composerExpanded()"
          [attr.aria-expanded]="composerExpanded()"
          aria-label="Open Chief of Staff"
          title="Dump a thought to your Chief of Staff"
        >
          <span aria-hidden="true">💬</span>
        </button>
        @if (timerError(); as err) {
          <span class="running-error" role="alert">{{ err }}</span>
        }
      </div>
      <!--
        Floating COS composer panel — only renders while the timer is active
        AND the user has tapped the CoS icon. Positioned above the bar like
        the breakdown preview card so it doesn't reshape the bar layout.
      -->
      @if (composerExpanded()) {
        <div class="composer-panel" role="dialog" aria-label="Chief of Staff composer">
          <div class="composer">
            <textarea
              #input
              [value]="draft()"
              (input)="onDraftInput($any($event.target).value)"
              (keydown)="onKey($event)"
              (paste)="onPaste($event)"
              [disabled]="busy()"
              [attr.placeholder]="placeholder()"
              rows="2"
            ></textarea>
            <input
              #fileInput
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              (change)="onFileSelected($any($event.target))"
            />
            <button
              type="button"
              class="attach"
              (click)="openFilePicker()"
              [disabled]="busy()"
              title="Attach a screenshot"
              aria-label="Attach a screenshot"
            >
              <span aria-hidden="true">📎</span>
            </button>
            <button
              type="button"
              class="ease"
              [class.active]="stuckMode()"
              (click)="toggleStuckMode()"
              [attr.aria-pressed]="stuckMode()"
              title="Ease mode: softer routing, briefer replies. Toggle when overwhelmed."
            >ease</button>
            <button
              type="button"
              class="send"
              (click)="send()"
              [disabled]="!draft().trim() || busy()"
              [attr.aria-label]="busy() ? 'Sending' : 'Send'"
            >
              @if (busy()) {
                <span class="spinner" aria-hidden="true"></span>
              } @else {
                <span aria-hidden="true">↑</span>
              }
            </button>
            <button
              type="button"
              class="composer-close"
              (click)="collapseComposer()"
              aria-label="Close composer"
              title="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>
      }
    } @else {
    <div class="composer">
      <textarea
        #input
        [value]="draft()"
        (input)="onDraftInput($any($event.target).value)"
        (keydown)="onKey($event)"
        (paste)="onPaste($event)"
        [disabled]="busy()"
        [attr.placeholder]="placeholder()"
        rows="1"
      ></textarea>
      <input
        #fileInput
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        (change)="onFileSelected($any($event.target))"
      />
      <button
        type="button"
        class="attach"
        (click)="openFilePicker()"
        [disabled]="busy()"
        title="Attach a screenshot"
        aria-label="Attach a screenshot"
      >
        <span aria-hidden="true">📎</span>
      </button>
      <button
        type="button"
        class="ease"
        [class.active]="stuckMode()"
        (click)="toggleStuckMode()"
        [attr.aria-pressed]="stuckMode()"
        title="Ease mode: softer routing, briefer replies. Toggle when overwhelmed."
      >ease</button>
      <button
        type="button"
        class="send"
        (click)="send()"
        [disabled]="!draft().trim() || busy()"
        [attr.aria-label]="busy() ? 'Sending' : 'Send'"
      >
        @if (busy()) {
          <span class="spinner" aria-hidden="true"></span>
        } @else {
          <span aria-hidden="true">↑</span>
        }
      </button>
    </div>
    }

    <div class="breakdown-pane" [class.busy]="breakdownBusy()">
      <input
        #breakdownInput
        type="text"
        class="bd-input"
        placeholder="what needs breaking?"
        [value]="breakdownText()"
        (input)="onBreakdownInput($any($event.target).value)"
        (keydown)="onBreakdownKey($event)"
        [disabled]="breakdownBusy()"
        aria-label="Break down a task (Cmd+Shift+B)"
      />
      <button
        type="button"
        class="bd-go"
        (click)="runBreakdown()"
        [disabled]="!breakdownText().trim() || breakdownBusy()"
        [attr.aria-label]="breakdownBusy() ? 'Breaking down' : 'Break down'"
        title="Enter to break down. Cmd+Shift+B focuses this input from anywhere."
      >
        @if (breakdownBusy()) {
          <span class="spinner" aria-hidden="true"></span>
        } @else {
          <span aria-hidden="true">↵</span>
        }
      </button>
      @if (breakdownError(); as err) {
        <div class="bd-error" role="alert">{{ err }}</div>
      }
    </div>
  </div>
</div>
